import React, { useEffect, useState } from "react";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import { MdKeyboardArrowRight } from "react-icons/md";
import chroma from "chroma-js";
import {
  CarDGrid,
  Card,
  CardHeaderText,
  BoldHeader,
  GraphCard,
  LargeContainer,
  TableWrapper,
  BigBoldText,
  Icon,
  PlainSelect,
  TrendContainer,
  AccountName,
  ButtonSpan,
} from "./home.style";
import CustomProgress from "../../../../components/Progress";
import { useNavigate } from "react-router-dom";

import CustomLoader from "../../../../components/CustomLoader";
import useReport from "../../../../hooks/useReport";
import { useReportWrapper, useStoreWrapper } from "../../../../store/store";
import _, { isEmpty } from "lodash";
import Maps from "../../../../components/Maps";
import dayjs from "dayjs";

const Home = () => {
  const navigate = useNavigate();

  const [filterDays, setFilterDays] = useState("0");
  const [facility, setFacility] = useState("");
  const [sortOrder, setSortOrder] = useState<string | null>(null);
  const [sortParam, setSortParam] = useState<string | null>(null);
  const [sortedList, setSortedList] = useState<any>([]);
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: dayjs().subtract(0, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  });

  const { user, userFacilities } = useStoreWrapper((state) => ({
    user: state.user,
    userFacilities: state.userFacilities,
  }));

  const { accountName } = user;

  const { setFacilitiesReportData } = useReportWrapper((state) => ({
    setFacilitiesReportData: state.setFacilitiesReportData,
  }));

  const { getDashboardReportByContactId } = useReport({
    contactId: user?.userId,
    filterDays: filterDays,
    facility: facility,
  });

  const {
    isLoading: dashboardLoading,
    data: dashboardData,
    refetch: fetchDashboardData,
  } = getDashboardReportByContactId;

  const _dashboardData = dashboardData?.data.data;

  useEffect(() => {
    fetchDashboardData();
  }, [filterDays, facility]);

  useEffect(() => {
    let total = _dashboardData?.totalIncidentCount;
    _dashboardData?.incidentTypeCount.forEach((item: any) => {
      item.valueInPercent = Math.round((item.count / total) * 100);
    });
  }, [_dashboardData?.incidentTypeCount, facility, filterDays]);

  useEffect(() => {
    const endDate = dayjs().format("MM-DD-YYYY");
    const startDate = dayjs()
      .subtract(parseInt(filterDays), "day")
      .format("MM-DD-YYYY");
    setDateRange({ startDate, endDate });
  }, [filterDays]);

  const sortList = (key: string) => {
    setSortParam(key);
    if (key !== sortParam) {
      setSortOrder("asc");
      setSortedList(_.orderBy(_dashboardData?.facilities, [key], ["asc"]));
      return;
    }

    if (sortOrder === null) {
      setSortOrder("asc");
      setSortedList(_.orderBy(_dashboardData?.facilities, [key], ["asc"]));
    } else if (sortOrder === "asc") {
      setSortOrder("desc");
      setSortedList(_.orderBy(_dashboardData?.facilities, [key], ["desc"]));
    } else if (sortOrder === "desc") {
      setSortOrder(null);
      setSortedList([]);
    }
  };

  return (
    <div style={{ minHeight: "100vh" }} title="dashboard">
      <div className="mb-10 mt-10">
        <AccountName>{accountName}</AccountName>
        <BigBoldText data-testid="home">Home</BigBoldText>
        <span className="float-right text-sm">
          <span className="mr-3">
            <PlainSelect
              onChange={(e) => setFacility(e.target.value)}
              id="facility-dropdown"
              data-testid="facility-dropdown"
            >
              <option value="">All Facilities</option>
              {userFacilities
                .sort((a: any, b: any) =>
                  a.facilityName > b.facilityName ? 1 : -1
                )
                ?.map((facility: any) => (
                  <option
                    style={{ marginBottom: "70px" }}
                    key={facility?.facilityId}
                    value={facility?.facilityId}
                  >
                    {facility?.facilityName}
                  </option>
                ))}
            </PlainSelect>
          </span>
          <span>
            <PlainSelect
              onChange={(e) => setFilterDays(e.target.value)}
              id="days-dropdown"
              data-testid="days-dropdown"
            >
              <option value="0">Total days</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
            </PlainSelect>
          </span>
        </span>
      </div>

      <CarDGrid>
        <Card
          onClick={() => {
            setFacilitiesReportData(_dashboardData?.facilities);
            navigate("/user/dashboard/facilities");
          }}
          data-testid="total-facilities-card"
        >
          <CardHeaderText>TOTAL NUMBER OF FACILITIES</CardHeaderText>
          <span className="float-right">
            {" "}
            <Icon>
              <MdKeyboardArrowRight color="grey" size={30} />
            </Icon>
          </span>
          <div className="mt-3">
            {dashboardLoading ? (
              <CustomLoader side />
            ) : (
              <div className="text-2xl font-black">
                {_dashboardData?.totalFacilitiesCount}
              </div>
            )}
          </div>
        </Card>

        <Card
          data-testid="total-incidents-card"
          onClick={() =>
            navigate(
              `/user/dashboard/incidents${
                filterDays !== "0"
                  ? `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
                  : ""
              }`
            )
          }
        >
          <CardHeaderText>TOTAL NUMBER OF INCIDENTS</CardHeaderText>
          <span className="float-right">
            <Icon>
              <MdKeyboardArrowRight color="grey" size={30} />
            </Icon>
          </span>
          <div className="mt-3">
            {dashboardLoading ? (
              <CustomLoader side />
            ) : (
              <div className="text-2xl font-black">
                {_dashboardData?.totalIncidentCount}
              </div>
            )}
          </div>
        </Card>

        <Card
          data-testid="total-staffs-card"
          onClick={() => navigate("/user/dashboard/active-staff")}
        >
          <CardHeaderText>TOTAL NUMBER OF ACTIVE STAFF</CardHeaderText>
          <span className="float-right">
            <Icon>
              <MdKeyboardArrowRight color="grey" size={30} />
            </Icon>
          </span>
          <div className="mt-3">
            {dashboardLoading ? (
              <CustomLoader side />
            ) : (
              <div className="text-2xl font-black">
                {_dashboardData?.totalStaffCount}
              </div>
            )}
          </div>
        </Card>
      </CarDGrid>

      <div className="sm:full flex flex-1 md:w-full lg:w-full">
        <GraphCard>
          <BoldHeader>Incident</BoldHeader>
          <TrendContainer>
            {dashboardLoading ? (
              <CustomLoader />
            ) : (
              <React.Fragment>
                {isEmpty(_dashboardData?.incidentTypeCount) ? (
                  <div className="flex flex-1 items-center justify-center">
                    <div className="text-xl font-medium text-gray-300">
                      Not available
                    </div>
                  </div>
                ) : (
                  <React.Fragment>
                    {_dashboardData?.incidentTypeCount?.map(
                      (incident: any, index: number) => (
                        <div className="mt-4" key={incident?.name}>
                          <CustomProgress
                            value={incident?.valueInPercent}
                            displayValue={incident?.count}
                            bgColor={chroma.random()}
                            label={incident?.name}
                            handleClick={(label: string) => {
                              navigate(
                                `/user/dashboard/incidents?incidentType=${label}`
                              );
                            }}
                          />
                        </div>
                      )
                    )}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </TrendContainer>
        </GraphCard>
      </div>

      <LargeContainer>
        <div className="mb-10">
          <BoldHeader>Incident by facility</BoldHeader>
        </div>

        <div className="flex flex-wrap lg:flex-nowrap">
          <div className="h-full w-full lg:w-1/2" style={{ height: "300px" }}>
            <Maps stateData={_dashboardData?.incidentStateCount || []} />
          </div>

          {/* // lg:px- */}
          <div className="w-full lg:w-1/2 lg:px-5">
            <TableWrapper>
              <div className="overflow-y-scroll" style={{ height: "300px" }}>
                {dashboardLoading ? (
                  <div style={{ textAlign: "center" }}>
                    <CustomLoader />
                  </div>
                ) : (
                  <table className="w-full table-auto border-collapse rounded border">
                    <thead>
                      <tr>
                        <th className="w-2/4 p-2 text-xs md:text-sm lg:text-base">
                          <ButtonSpan
                            className="cursor-pointer"
                            onClick={() => sortList("name")}
                          >
                            Facility
                          </ButtonSpan>
                          {sortParam === "name" && (
                            <span>
                              {sortOrder === "asc" ? (
                                <FaSortUp
                                  color="lightgray"
                                  className="cursor-pointer"
                                />
                              ) : (
                                <FaSortDown
                                  color="lightgray"
                                  className="cursor-pointer"
                                />
                              )}
                            </span>
                          )}
                        </th>
                        <th className="w-1/4 p-2 text-xs md:text-sm lg:text-base">
                          <ButtonSpan
                            className="cursor-pointer"
                            onClick={() => sortList("directorOfNursing")}
                          >
                            DON
                          </ButtonSpan>
                          {sortParam === "directorOfNursing" && (
                            <span>
                              {sortOrder === "asc" ? (
                                <FaSortUp
                                  color="lightgray"
                                  className="cursor-pointer"
                                />
                              ) : (
                                <FaSortDown
                                  color="lightgray"
                                  className="cursor-pointer"
                                />
                              )}
                            </span>
                          )}
                        </th>
                        <th className="w-1/4 p-2 text-xs md:text-sm lg:text-base">
                          <ButtonSpan
                            className="cursor-pointer"
                            onClick={() => sortList("incidentNumber")}
                          >
                            Total incident
                          </ButtonSpan>
                          {sortParam === "incidentNumber" && (
                            <span>
                              {sortOrder === "asc" ? (
                                <FaSortUp
                                  color="lightgray"
                                  className="cursor-pointer"
                                />
                              ) : (
                                <FaSortDown
                                  color="lightgray"
                                  className="cursor-pointer"
                                />
                              )}
                            </span>
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sortedList?.length > 0
                        ? sortedList
                        : _dashboardData?.facilities
                      )?.map((item: any) => (
                        <tr
                          key={item?.facilityId}
                          className="cursor-pointer border-t hover:bg-gray-100"
                          onClick={() =>
                            navigate(
                              `/user/dashboard/incidents?facilityName=${item.facilityId}`
                            )
                          }
                        >
                          <td className="w-2/4 whitespace-normal px-2 py-3 text-center text-xs md:text-sm lg:text-base">
                            {item?.name}
                          </td>
                          <td className="w-1/4 whitespace-normal px-2 py-3 text-center text-xs md:text-sm lg:text-base">
                            {item?.directorOfNursing || "Not Available"}
                          </td>
                          <td className="w-1/4 whitespace-normal px-2 py-3 text-center text-xs md:text-sm lg:text-base">
                            {item?.incidentNumber}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </TableWrapper>
          </div>
        </div>
      </LargeContainer>
    </div>
  );
};

export default Home;
